import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../ui/dialog";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Switch } from "../ui/switch";
import { Button } from "../ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Field } from "./useAdminApi";
import { FileField } from "./FileField";
import { TimingRecorder } from "./TimingRecorder";

const EMPTY = { title: "", youtube: "", theme: "", language: "fr", calm: false, lyrics: "", published: true, position: 0, audio_path: null, timings: "" };

export const SongForm = ({ open, onOpenChange, song, themes, onSave, call }) => {
  const [f, setF] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setF(song ? { ...EMPTY, ...song, youtube: `https://youtu.be/${song.youtube_id}` } : { ...EMPTY, theme: themes[0]?.slug || "" });
  }, [open, song, themes]);

  const set = (k) => (v) => setF((s) => ({ ...s, [k]: v }));
  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { title, youtube, theme, language, calm, lyrics, published, position, audio_path, timings } = f;
      await onSave({ title, youtube, theme, language, calm, lyrics, published, position: Number(position) || 0, audio_path: audio_path || null, timings: timings || "" });
      onOpenChange(false);
    } catch { /* toast shown */ } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[92dvh] overflow-y-auto rounded-[28px] sm:max-w-2xl" data-testid="song-form-dialog">
        <DialogHeader><DialogTitle>{song ? "Modifier la comptine" : "Nouvelle comptine"}</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <Field label="Titre"><Input required value={f.title} onChange={(e) => set("title")(e.target.value)} data-testid="song-form-title" /></Field>
          <Field label="Lien ou identifiant YouTube"><Input required value={f.youtube} onChange={(e) => set("youtube")(e.target.value)} placeholder="https://youtu.be/…" data-testid="song-form-youtube" /></Field>
          <div className="grid gap-4 sm:grid-cols-3">
            <Field label="Thème">
              <Select value={f.theme} onValueChange={set("theme")}>
                <SelectTrigger data-testid="song-form-theme"><SelectValue placeholder="Choisir" /></SelectTrigger>
                <SelectContent>{themes.map((t) => <SelectItem key={t.slug} value={t.slug}>{t.name}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Langue">
              <Select value={f.language} onValueChange={set("language")}>
                <SelectTrigger data-testid="song-form-language"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="fr">Français</SelectItem><SelectItem value="en">English</SelectItem></SelectContent>
              </Select>
            </Field>
            <Field label="Ordre"><Input type="number" value={f.position} onChange={(e) => set("position")(e.target.value)} data-testid="song-form-position" /></Field>
          </div>
          <div className="flex flex-wrap gap-6">
            <label className="flex items-center gap-2 font-semibold"><Switch checked={f.calm} onCheckedChange={set("calm")} data-testid="song-form-calm" /> Calme (Mode dodo)</label>
            <label className="flex items-center gap-2 font-semibold"><Switch checked={f.published} onCheckedChange={set("published")} data-testid="song-form-published" /> Publiée</label>
          </div>
          <Field label="Paroles (une ligne vide entre les couplets)">
            <Textarea rows={12} value={f.lyrics} onChange={(e) => set("lyrics")(e.target.value)} data-testid="song-form-lyrics" />
          </Field>
          <Field label="Musique du karaoké (MP3, sans voix)">
            <div className="flex flex-wrap items-center gap-3">
              <FileField kind="audio" value={f.audio_path} onChange={set("audio_path")} call={call} testId="song-form-audio-input" />
              {f.audio_path && <Button type="button" variant="ghost" size="sm" onClick={() => set("audio_path")(null)} data-testid="song-form-audio-remove">Retirer la musique</Button>}
            </div>
          </Field>
          <Field label="Minutage (une ligne par vers : minutes:secondes.dixièmes puis le vers)">
            <Textarea rows={8} value={f.timings} onChange={(e) => set("timings")(e.target.value)} className="font-mono text-xs" data-testid="song-form-timings" />
          </Field>
          <TimingRecorder song={song} lyrics={f.lyrics} onDone={set("timings")} />
          <DialogFooter>
            <Button type="submit" disabled={saving || !f.theme} className="rounded-full bg-[#38B6FF] hover:bg-[#1fa3ee]" data-testid="song-form-save">Enregistrer</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
